import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './CloudPartnerDetails.styles'
import {
  AutoComplete,
  ExternalLink,
  TextInputV2,
} from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import type { FlyOutCloudSettingsFormValues } from '../../types'
import { fetchAwsCountriesList } from '../../../../common/api'
import { AwsCountriesList } from '../../../AddEditProductFormComponents/FlyOutOfferAvailabilityByCountry/FlyOutProductOfferAvailabilityByCountry'
import { Options } from '@labrav/react-components/lib/@types/v2/components/AutoComplete'
import { awsSellerBusinessLocationUrl } from '../../../../common/utils/constants'

export const CloudPartnerDetails: React.FC = () => {
  const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
    useFormikContext() as FormikProps<FlyOutCloudSettingsFormValues>
  const { classes } = useStyles()
  const { awsAccountId, awsAccountName, countryRegistered } = values

  const [awsCountriesLists, setAwsCountriesList] = useState<AwsCountriesList>({
    countries: [],
  })

  useEffect(() => {
    fetchAwsCountriesList()
      .then(response => {
        setAwsCountriesList(response.data)
      })
      .catch(error => {
        console.error('Failed to fetch countries list:', error)
      })
  }, [])

  const handleCountrySelection = useCallback(
    async (
      _: React.SyntheticEvent,
      newValue: string | { key: string | undefined; label: string } | null
    ) => {
      await setFieldValue(
        'countryRegistered',
        newValue
          ? (newValue as { key: string | undefined; label: string }).key
          : ''
      )
    },
    [values, setFieldValue]
  )
  const allCountryNames = useMemo(
    () =>
      awsCountriesLists?.countries?.map(country => ({
        key: country.code,
        label: country.country,
      })) || [],
    [awsCountriesLists?.countries]
  )
  const selectedCountryValue = useMemo(
    () =>
      allCountryNames.find(country => country.key === countryRegistered) || {
        key: '',
        label: '',
      },
    [allCountryNames, countryRegistered]
  )

  return (
    <div className={classes.fieldsContainer}>
      <div className={classes.textContainer}>
        <div>
          <Typography className={classes.sectionText}>
            Please fill your 12-digit AWS account ID.
          </Typography>
        </div>
        <div className={classes.field}>
          <TextInputV2
            keyName="awsAccountId"
            title="AWS account ID"
            type="text"
            placeHolder=""
            value={awsAccountId as string}
            isError={!!(errors?.awsAccountId && touched?.awsAccountId)}
            onChange={handleChange}
            isReadOnly={false}
            isRequired={false}
            errorText={(!!touched?.awsAccountId && errors?.awsAccountId) || ''}
            showSpinner={false}
            onBlur={handleBlur}
          />
        </div>
      </div>
      <div className={classes.textContainer}>
        <div className={classes.field}>
          <TextInputV2
            keyName="awsAccountName"
            title="AWS account name"
            type="text"
            placeHolder=""
            value={awsAccountName as string}
            isError={!!(errors.awsAccountName && touched.awsAccountName)}
            onChange={handleChange}
            onBlur={handleBlur}
            isReadOnly={false}
            isRequired={false}
            errorText={
              (!!touched.awsAccountName && errors.awsAccountName) || ''
            }
            showSpinner={true}
            supportingText={
              'E.g. marketplace_account or seller_environment or abcxyz123'
            }
          />
        </div>
      </div>
      <div className={classes.field}>
        <AutoComplete
          onChange={handleCountrySelection}
          id={'countryRegistered'}
          label={'Select a country'}
          options={allCountryNames as Options[]}
          value={selectedCountryValue}
          error={!!(errors?.countryRegistered && touched?.countryRegistered)}
          helperText={
            !!touched?.countryRegistered &&
            errors?.countryRegistered &&
            errors?.countryRegistered
          }
          onBlur={handleBlur}
          className={classes.disabled}
        />
        <div className={classes.subText}>
          Select the country that is added as the{' '}
          <span>
            {' '}
            <ExternalLink
              href={awsSellerBusinessLocationUrl}
              children={
                <span className={classes.urlBoldText}>Business location</span>
              }
            />
          </span>{' '}
          on AWS Marketplace management portal.
        </div>
      </div>
    </div>
  )
}
